import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { loginUser } from "../loginSlice";
import "./login.css";
import { setBackButtonUrl } from "../uiSlice";
import { selectAdminToken } from "../../admin/sessionSlice";
import Swal from "sweetalert2";
import logoImg from "../images/desktop/logo.png";

const RESUME_WAND_PLACEHOLDER = "__hp_resume__";

function Login() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const adminToken = useSelector(selectAdminToken);
  const { status, user, error } = useSelector((state) => state.auth);
  const [reportChecking, setReportChecking] = useState(false);
  const resumeRouteDoneRef = useRef(false);
  const sentToConfigureRef = useRef(false);
  const fromMobileAppFromQuery = (() => {
    const raw = new URLSearchParams(window.location.search).get("fromMobileApp");
    const normalized = String(raw || "").trim().toLowerCase();
    return normalized === "true" || normalized === "1" || normalized === "yes";
  })();

  useEffect(() => {
    if (fromMobileAppFromQuery) {
      sessionStorage.setItem("fromMobileApp", "true");
    } else {
      sessionStorage.removeItem("fromMobileApp");
    }
  }, [fromMobileAppFromQuery]);

  useEffect(() => {
    dispatch(loginUser());
  }, [dispatch]);

  useEffect(() => {
    if (status === "succeeded" && user?.backButtonRedirect) {
      dispatch(setBackButtonUrl(user.backButtonRedirect));
    }
  }, [status, user, dispatch]);

  useEffect(() => {
    if (status !== "failed") return;
    const msg = error ? String(error) : "Session expired";
    const redirect =
      user && user.backButtonRedirect ? user.backButtonRedirect : "/";
    Swal.fire("Session Expired!", msg, "error").then(() => {
      window.location.href = redirect;
    });
  }, [status, error, user]);

  useEffect(() => {
    if (status !== "succeeded" || !user) return;

    const isDemo =
      user.source === "DEMO" ||
      String(user.role || user.user_type || "").toLowerCase() === "demobypass";
    // Demo flow doesn't have admin token / report resume. Stay on login and wait for user to click "Begin Play".
    if (isDemo) return;

    if (user?.guessMePlayed === true && !resumeRouteDoneRef.current) {
      resumeRouteDoneRef.current = true;
      const back = user.backButtonRedirect || "/";
      Swal.fire("Already played", " ", "error").then(() => {
        window.location.href = back;
      });
      return;
    }

    const uid = user.userId || user.userid || user.id;
    const sid = user.sessionId;
    const oid = user.organizationId;
    if (!uid || !sid || !oid || !adminToken) return;
    if (resumeRouteDoneRef.current) return;

    const backendBase = String(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "");
    if (!backendBase) return;

    let cancelled = false;
    // Check existing report in background; do not block the login screen.
    setReportChecking(true);

    (async () => {
      try {
        const res = await fetch(`${backendBase}/fetchReport`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${adminToken}`,
          },
          body: JSON.stringify({
            userId: String(uid),
            sessionId: String(sid),
            organizationId: String(oid),
          }),
        });
        const data = await res.json().catch(() => ({}));
        if (cancelled || resumeRouteDoneRef.current) return;

        const back = user.backButtonRedirect || "/";
        const rpt = data.success ? data.report : null;

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          await Swal.fire("You have already played!", " ", "error");
          window.location.href = back;
          return;
        }

        if (rpt && Number(rpt.gameover) === 1) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : 0;
          navigate("/stage2/broom", {
            replace: true,
            state: {
              wandId: RESUME_WAND_PLACEHOLDER,
              points: Number.isFinite(s1) ? s1 : 0,
              time: rpt.time != null ? String(rpt.time) : "",
              reportId: rpt.reportId,
              stageId: rpt.id != null ? rpt.id : undefined,
            },
          });
          return;
        }

        if (rpt && Number(rpt.stage2) > 0 && Number(rpt.gameover) !== 2) {
          resumeRouteDoneRef.current = true;
          const s1 = rpt.stage1 != null ? Number(rpt.stage1) : NaN;
          const s2 = rpt.stage2 != null ? Number(rpt.stage2) : NaN;
          const pts = Number(rpt.points) || (Number.isFinite(s1) && Number.isFinite(s2) ? s1 + s2 : 0);
          navigate("/thankyou", {
            replace: true,
            state: {
              points: pts,
              time: rpt.time != null ? String(rpt.time) : "",
              stageId: rpt.id != null ? rpt.id : undefined,
              spellScore: Number.isFinite(s1) ? s1 : pts - (Number.isFinite(s2) ? s2 : 0),
              flightScore: Number.isFinite(s2) ? s2 : 0,
            },
          });
          return;
        }

        // No resume route; stay on login and wait for the user to click "Begin Play".

      } catch (e) {
        console.error("login fetchReport:", e);
      } finally {
        if (!cancelled) setReportChecking(false);
      }
    })();

    return () => {
      cancelled = true;
      setReportChecking(false);
    };
  }, [status, user, adminToken, navigate]);

  if (status === "loading") {
    return (
      <div className="login-main-container">
        <div className="quiz-loader-container">
          <div className="quiz-loader"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="login-main-container">
      <img src={logoImg} className="login-logo1" alt="Game Logo" />
      {user && status === "succeeded" && (
        <div>
          {user?.guessMePlayed ? (
            <button
              type="button"
              className="begin-play-btn"
              aria-label="Already played"
              onClick={() => {
                const back = user.backButtonRedirect || "/";
                Swal.fire("Already played", " ", "error").then(() => {
                  window.location.href = back;
                });
              }}
              style={{ opacity: 0.7, cursor: "not-allowed", border: "none", background: "transparent" }}
            >
              <span className="begin-play-pill">ALREADY PLAYED</span>
            </button>
          ) : (
            <Link
              to={user?.lobby ? "/game" : "/configure-game-settings"}
              state={user?.lobby ? { lobby_name: user.lobby } : undefined}
              className="begin-play-btn"
              aria-label="Begin play"
            >
              <span className="begin-play-pill">BEGIN PLAY</span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

export default Login;
